import { computed, Injectable, signal } from '@angular/core';
import { ScreenOrientation, ScreenSize, ScreenWidth } from 'app/shared/enums';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public get screenSize(): ScreenSize {
    if (window.innerWidth < ScreenWidth.MOBILE) {
      return ScreenSize.MOBILE;
    }
    if (window.innerWidth < ScreenWidth.TABLET) {
      return ScreenSize.TABLET;
    }
    return ScreenSize.DESKTOP;
  }

  public get screenOrientation(): ScreenOrientation {
    if (window.innerHeight > window.innerWidth) {
      return 'portrait';
    }
    return 'landscape';
  }

  private screenSizeSignal = signal(this.screenSize);
  public screenOrientationSignal = signal(this.screenOrientation);
  public isDesktop = computed(() => this.screenSizeSignal() === ScreenSize.DESKTOP);
  public isPortrait = new BehaviorSubject<boolean>(this.screenOrientation === 'portrait');

  public resize$ = new BehaviorSubject<ScreenSize>(this.screenSize);
  public theme = new BehaviorSubject<string>(this.getTheme());

  public darkMode$ = this.theme.asObservable().pipe(
    map((theme) => {
      const isDarkMode = theme === 'dark';
      sessionStorage.setItem('theme', theme);
      return isDarkMode;
    })
  );

  public avatarColors$ = this.darkMode$.pipe(
    map((isDarkMode) => {
      return {
        backgroundColor: isDarkMode ? 'primary-20' : 'primary-90',
        color: isDarkMode ? 'neutral-90' : 'primary-10',
      };
    })
  );

  public triggerResize(): void {
    this.resize$.next(this.screenSize);
    this.screenSizeSignal.set(this.screenSize);
    const orientation = this.screenOrientation;
    this.screenOrientationSignal.set(orientation);
    this.isPortrait.next(orientation === 'portrait');
  }

  public toggleMode(): void {
    const isDarkMode = this.theme.value === 'dark';
    const nextTheme = isDarkMode ? 'light' : 'dark';
    this.theme.next(nextTheme);
    document.body.classList.toggle('dark');
  }

  private getTheme(): string {
    const savedTheme = sessionStorage.getItem('theme') ?? 'light';
    if (savedTheme === 'dark') {
      document.body.classList.add('dark');
    }
    return savedTheme;
  }
}
